import Campaign from '../store/Campaign';
import React, { HTMLAttributes } from 'react';
import { BannerStyles } from 'mini-banner/lib/schema/Banner.types';
import { CAMPAIGN_CLICKED, CAMPAIGN_DISMISSED } from '../utils/eventNames';
import { CampaignEndSignal } from '../store/schema/CampaignEndSignal';
import { CampaignQueue } from '../store/CampaignQueue';
import { CampaignType } from '../store/schema/CampaignType';
import { CustomData } from 'mini-app-context';
import { endCampaign } from '../utils/endCampaign';
import { getDataLogger, LogOptions } from 'telemetry';
import { getIconImageSource } from './utils/getImageSourceUtil';
import { getModifiedViewProps } from './utils/getModifiedViewProps';
import { IconInfo } from 'mini-embedded-messagebox';
import { OverlayBanner } from 'mini-overlay-banner';

interface CustomLogParamsType {
    logData?: CustomData;
    logOptions?: LogOptions;
}

export interface CampaignOverlayBannerConfig {
    title: string;
    subtitle: string;
    icon: IconInfo | Function;
    closeButtonAriaLabel: string;
    primaryButtonText: string;
    secondaryButtonText: string;
    tertiaryButtonText: string;
    onPrimaryButtonClick?: () => void;
    onSecondaryButtonClick?: () => void;
    onTertiaryButtonClick?: () => void;
    onRender?: () => void;
    imageSrc?: string;
    refId?: string;
    coverImageURL?: string;
    onCampaignDismiss?: () => void;
    onCampaignMessageClick?: () => void;
    onMount?: () => void;
    customLogParams?: CustomLogParamsType;
    withoutBubble?: boolean;
    bannerStyles?: BannerStyles;
    messageWrapperElementProps?: HTMLAttributes<HTMLDivElement>;
    rootElementProps?: HTMLAttributes<HTMLDivElement>;
    elementToFocusOnOpen?: React.RefObject<any>;
    elementToFocusOnClose?: React.RefObject<any>;
}

/**
 * Class to instantiate a banner campaign type object.
 */
export class CampaignOverlayBanner implements CampaignType<CampaignOverlayBannerConfig> {
    logger = getDataLogger();
    private static overlayBannerQueue = CampaignQueue.getInstance();

    public start(campaign: Campaign<CampaignOverlayBannerConfig>) {
        CampaignOverlayBanner.overlayBannerQueue.addToQueue(campaign);
    }

    public end(campaign: Campaign<CampaignOverlayBannerConfig>, endSignal: CampaignEndSignal) {
        endCampaign(campaign, endSignal);
    }

    private logCampaignUsage(
        eventName: string,
        campaign: Campaign<CampaignOverlayBannerConfig>,
        customLogParams?: CustomLogParamsType
    ) {
        this.logger?.(
            eventName,
            { id: campaign.getRemoteCampaignId(), isUIAction: true, ...customLogParams?.logData },
            { isCore: true, ...customLogParams?.logOptions }
        );
    }

    private logAndEndCampaign(
        eventName: string,
        campaign: Campaign<CampaignOverlayBannerConfig>,
        endSignal: CampaignEndSignal,
        customLogParams?: CustomLogParamsType
    ) {
        // Log usage DP
        this.logCampaignUsage(eventName, campaign, customLogParams);

        // End the campaign
        this.end(campaign, endSignal);
    }

    /**
     * This is called from the render function of the Campaign object
     * @param campaign Campaign object instance
     * @param viewProps Props to be passed to the view component
     */
    public render(
        campaign: Campaign<CampaignOverlayBannerConfig>,
        viewProps: CampaignOverlayBannerConfig
    ): JSX.Element {
        const self = this;
        const {
            onCampaignDismiss,
            customLogParams,
            onPrimaryButtonClick,
            onSecondaryButtonClick,
            onTertiaryButtonClick,
            onRender,
            elementToFocusOnOpen,
            elementToFocusOnClose,
        } = viewProps;
        onRender?.();

        const onOverlayClick = () => {
            onCampaignDismiss?.();
            self.logAndEndCampaign(
                CAMPAIGN_DISMISSED,
                campaign,
                CampaignEndSignal.OnUserDismiss,
                customLogParams
            );
        };

        const primaryButtonHandler = () => {
            onPrimaryButtonClick?.();
            const customDataToBeLogged: CustomLogParamsType = {
                logData: { ...customLogParams?.logData, buttonType: 'Primary' },
                logOptions: customLogParams?.logOptions,
            };
            self.logAndEndCampaign(
                CAMPAIGN_CLICKED,
                campaign,
                CampaignEndSignal.OnClick,
                customDataToBeLogged
            );
        };

        const secondaryButtonHandler = () => {
            onSecondaryButtonClick?.();
            const customDataToBeLogged: CustomLogParamsType = {
                logData: { ...customLogParams?.logData, buttonType: 'Secondary' },
                logOptions: customLogParams?.logOptions,
            };
            self.logAndEndCampaign(
                CAMPAIGN_CLICKED,
                campaign,
                CampaignEndSignal.OnClick,
                customDataToBeLogged
            );
        };

        const tertiaryButtonHandler = () => {
            onTertiaryButtonClick?.();
            const customDataToBeLogged: CustomLogParamsType = {
                logData: { ...customLogParams?.logData, buttonType: 'Tertiary' },
                logOptions: customLogParams?.logOptions,
            };
            self.logAndEndCampaign(
                CAMPAIGN_CLICKED,
                campaign,
                CampaignEndSignal.OnClick,
                customDataToBeLogged
            );
        };

        return (
            <OverlayBanner
                {...viewProps}
                {...getModifiedViewProps(viewProps)}
                coverImageURL={getIconImageSource(viewProps)}
                onOverlayClick={onOverlayClick}
                onPrimaryButtonClick={primaryButtonHandler}
                onSecondaryButtonClick={secondaryButtonHandler}
                onTertiaryButtonClick={tertiaryButtonHandler}
                elementToFocusOnClose={elementToFocusOnClose}
                elementToFocusOnOpen={elementToFocusOnOpen}
            />
        );
    }
}
